import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { BedDouble, MapPin, ShieldCheck, Star, ArrowLeft, Heart } from "lucide-react";
import { toast } from "sonner";

const defaultImg = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [open, setOpen] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [refForm, setRefForm] = useState({ client_name: user?.full_name || "", client_phone: "", client_email: user?.email || "", notes: "", veteran_status: false });

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setListing(r.data));
    if (user?.role === "seeker") {
      api.get("/favorites").then((r) => {
        setFavorited(r.data.some((l) => l.id === id));
      });
    }
  }, [id, user]);

  if (!listing) return <div className="p-12 text-center text-[#4A5568]">Loading…</div>;

  const toggleFavorite = async () => {
    try {
      if (favorited) {
        await api.delete(`/favorites/${id}`);
        setFavorited(false);
        toast.success("Removed from favorites");
      } else {
        await api.post(`/favorites/${id}`);
        setFavorited(true);
        toast.success("Saved to favorites");
      }
    } catch { toast.error("Sign in to save favorites"); }
  };

  const submitReferral = async () => {
    try {
      await api.post("/referrals", { listing_id: id, ...refForm });
      toast.success("Referral sent");
      setOpen(false);
      setRefForm({ client_name: "", client_phone: "", client_email: "", notes: "", veteran_status: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit referral");
    }
  };

  return (
    <div data-testid="listing-detail" className="max-w-6xl mx-auto px-6 sm:px-8 py-10">
      <Link to="/search" className="text-sm text-[#1E3A8A] mb-4 inline-flex items-center gap-1.5" data-testid="back-to-search">
        <ArrowLeft className="w-4 h-4" /> Back to search
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-4">
        <div className="lg:col-span-2">
          <img src={listing.photo_url || defaultImg} alt={listing.name} className="w-full h-96 object-cover rounded-sm border border-[#D1D5DB]" />
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-2">
              {listing.provider_featured && <Badge className="bg-[#FEEBC8] text-[#7B341E] border-0"><Star className="w-3 h-3 mr-1" /> Featured</Badge>}
              {listing.provider_verified && <Badge className="bg-[#C6F6D5] text-[#22543D] border-0"><ShieldCheck className="w-3 h-3 mr-1" /> Verified Provider</Badge>}
              <Badge variant="outline">{listing.housing_type.replace('_', ' ')}</Badge>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">{listing.name}</h1>
            <div className="text-[#4A5568] mt-2 flex items-center gap-1.5"><MapPin className="w-4 h-4" />{listing.address}, {listing.city}, {listing.state} {listing.zip_code}</div>
            <div className="mt-6 prose prose-sm max-w-none text-[#4A5568] whitespace-pre-line">{listing.description || "No description provided."}</div>
            {listing.amenities?.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-[#0A192F] font-semibold mb-2">Amenities</div>
                <div className="flex flex-wrap gap-2">
                  {listing.amenities.map((a) => <Badge key={a} variant="outline" className="border-[#D1D5DB]">{a}</Badge>)}
                </div>
              </div>
            )}
            {listing.eligibility && (
              <div className="mt-6 bg-[#F5F5F0] border-l-4 border-[#C05621] p-4">
                <div className="text-xs uppercase tracking-widest text-[#C05621] font-semibold mb-1">Eligibility</div>
                <div className="text-sm text-[#0A192F]">{listing.eligibility}</div>
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="bg-white border border-[#D1D5DB] p-6 rounded-sm sticky top-24">
            <div className="text-xs uppercase tracking-widest text-[#C05621] font-semibold">Real-time availability</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-heading text-5xl font-bold text-[#0A192F]">{listing.available_beds}</span>
              <span className="text-[#4A5568]">of {listing.total_beds} beds</span>
            </div>
            <div className="mt-4 pt-4 border-t border-[#D1D5DB] space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#4A5568]">Monthly</span><span className="font-semibold text-[#0A192F]">{listing.monthly_rent > 0 ? `$${listing.monthly_rent}` : "Free"}</span></div>
              <div className="flex justify-between"><span className="text-[#4A5568]">Gender</span><span className="font-semibold text-[#0A192F] capitalize">{listing.gender}</span></div>
              <div className="flex justify-between"><span className="text-[#4A5568]">Veteran-only</span><span className="font-semibold text-[#0A192F]">{listing.veteran_only ? "Yes" : "No"}</span></div>
              <div className="flex justify-between"><span className="text-[#4A5568]">Provider</span><span className="font-semibold text-[#0A192F]">{listing.provider_name}</span></div>
            </div>
            <div className="mt-5 space-y-2">
              {user?.role === "seeker" && (
                <Button variant="outline" data-testid="toggle-favorite" onClick={toggleFavorite} className={`w-full ${favorited ? "border-[#C05621] text-[#C05621]" : "border-[#0A192F] text-[#0A192F]"}`}>
                  <Heart className={`w-4 h-4 mr-2 ${favorited ? "fill-[#C05621]" : ""}`} /> {favorited ? "Saved" : "Save to Favorites"}
                </Button>
              )}
              {user ? (
                user.role === "case_manager" || user.role === "seeker" ? (
                  <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="refer-btn" className="w-full bg-[#C05621] hover:bg-[#9C4221] text-white">
                        {user.role === "seeker" ? "Request Placement" : "Send Referral"}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{user.role === "seeker" ? "Request placement" : "Send referral"} — {listing.name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label>Client / Applicant name</Label>
                          <Input data-testid="referral-name" value={refForm.client_name} onChange={(e) => setRefForm({ ...refForm, client_name: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Phone</Label>
                            <Input data-testid="referral-phone" value={refForm.client_phone} onChange={(e) => setRefForm({ ...refForm, client_phone: e.target.value })} />
                          </div>
                          <div>
                            <Label>Email</Label>
                            <Input data-testid="referral-email" value={refForm.client_email} onChange={(e) => setRefForm({ ...refForm, client_email: e.target.value })} />
                          </div>
                        </div>
                        <div>
                          <Label>Notes</Label>
                          <Textarea data-testid="referral-notes" rows={4} value={refForm.notes} onChange={(e) => setRefForm({ ...refForm, notes: e.target.value })} placeholder="Background, urgency, services needed…" />
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox data-testid="referral-veteran" checked={refForm.veteran_status} onCheckedChange={(v) => setRefForm({ ...refForm, veteran_status: !!v })} /> Veteran
                        </label>
                      </div>
                      <DialogFooter>
                        <Button data-testid="submit-referral" disabled={!refForm.client_name} onClick={submitReferral} className="bg-[#1E3A8A] hover:bg-[#1A365D]">Submit Referral</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <div className="text-xs text-[#4A5568]">You are signed in as {user.role.replace('_',' ')}. Switch to a seeker or case manager account to refer.</div>
                )
              ) : (
                <Link to="/login" data-testid="login-to-refer"><Button className="w-full bg-[#1E3A8A] hover:bg-[#1A365D] text-white">Sign in to refer</Button></Link>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
